/*
 * script-navigation.js — the nav chrome's single controller.
 *
 * OWNERSHIP CONTRACT (full inversion — see the header-state refactor):
 * every element navigation.liquid renders (#index-logo, #index-menu, #nav-bg,
 * #cart-*) is written by THIS file and nobody else. No other script may set
 * styles/classes on nav elements or bind listeners to them. Instead:
 *
 *   state IN  — content scripts REPORT what the page is showing:
 *                 HeaderState.set({ view: 'pdp' })
 *                 HeaderState.set({ view: 'home', atTop: true })
 *                 HeaderState.openCart() / closeCart()
 *               The nav derives its own presentation from the state record and
 *               runs the appropriate transition itself.
 *
 *   intents OUT — the nav owns clicks on its elements and emits semantic
 *               DOM events for content work it can't do itself:
 *                 'nav:home' — the logo was activated while a PDP swap is up;
 *                              script-index-product.js soft-restores home.
 *
 * State record (HeaderState.state):
 *   view      — 'home' | 'collection' | 'filter' | 'viewall' | 'pdp'
 *   atTop     — home scroll is above the 35px threshold (only meaningful for
 *               view 'home'; frozen while another view owns the document scroll)
 *   barPinned — logo was clicked at the home top: bar force-shown over home
 *               until the next scroll-return-to-top or view change
 *   cartOpen  — cart drawer open
 *   (compat, derived: modalOpen, menuLocked, filtersShown)
 *
 * Presentation is a pure function of the record — see chromeFor(). Because
 * every actor that moves the document scroll must ping set() anyway (it is
 * reporting the view change that moved it), the nav re-derives atTop at
 * exactly the moments the old sentinel went deaf; the stale-latch class of
 * bug ("logo over the filter buttons") is structurally gone.
 *
 * Load order: theme.liquid loads this in <head> before script-cart.js and
 * before the section scripts, so the renderer attaches before any content
 * script can ping.
 */

(function () {
  var state = {
    view: 'home',
    atTop: true,
    barPinned: false,
    cartOpen: false,
  };

  var subscribers = [];

  function snapshot() {
    var modal = state.view === 'collection' || state.view === 'filter' || state.view === 'viewall';
    return {
      view: state.view,
      atTop: state.atTop,
      barPinned: state.barPinned,
      cartOpen: state.cartOpen,
      // Legacy derived fields — kept so old readers (and dead code awaiting
      // removal) see coherent values.
      modalOpen: modal,
      menuLocked: modal || state.barPinned,
      filtersShown: true,
    };
  }

  function notify(next, prev) {
    subscribers.forEach(function (fn) {
      try { fn(next, prev); } catch (e) { /* subscriber threw — keep going */ }
    });
  }

  function noop() {}

  /*
   * Chrome derivation — the single source of truth for "what should the nav be
   * showing", used by the renderer (below) AND by openCart, which needs to know
   * whether the logo was already up before the cart took over. Defined here, in
   * the state IIFE, so both see the same rule; the renderer picks it up off
   * _chromeFor rather than keeping a second copy that could drift.
   */
  function chromeFor(s) {
    if (s.view === 'pdp') return { logo: true, menu: false };   // logo = go-home affordance
    if (s.view !== 'home') return { logo: false, menu: true };  // collection/filter/viewall
    if (s.barPinned) return { logo: false, menu: true };
    return { logo: s.atTop, menu: !s.atTop };
  }

  // Attached by the renderer block below once its DOM refs exist. Absent on
  // pages without the index menu (hard product page, information, …) — there
  // set() just records state, which is exactly right: the cart still needs a
  // truthful cartOpen, and there is no index chrome to render.
  var render = null;

  window.HeaderState = {
    get state() { return snapshot(); },

    subscribe: function (fn) {
      if (typeof fn !== 'function') return noop;
      subscribers.push(fn);
      return function () {
        var i = subscribers.indexOf(fn);
        if (i !== -1) subscribers.splice(i, 1);
      };
    },

    /*
     * The one write API for view/scroll state.
     *   patch: { view?, atTop?, barPinned? }
     *   opts:  { transition?: 'instant', logoRevealMs?, force? }
     *
     * When `view` changes and the patch doesn't pin atTop explicitly, atTop is
     * RE-DERIVED from the live scroll position — callers therefore ping AFTER
     * restoring the document scroll, or pass the target atTop themselves when
     * the restore hasn't happened yet (see closeModal in the index scripts).
     */
    set: function (patch, opts) {
      patch = patch || {};
      opts = opts || {};
      var prev = snapshot();

      if (patch.view !== undefined && patch.view !== state.view) {
        state.view = patch.view;
        state.barPinned = false; // a view change absorbs/clears the pin
        if (patch.atTop === undefined) {
          state.atTop = (window.scrollY || window.pageYOffset || 0) <= 35;
        }
      }
      if (patch.atTop !== undefined) state.atTop = !!patch.atTop;
      if (patch.barPinned !== undefined) state.barPinned = !!patch.barPinned;
      // Scroll-return-to-top releases the pin (matches the old menuLocked
      // clear in _onScrolledToTop).
      if (state.view === 'home' && state.atTop && patch.atTop !== undefined && patch.barPinned === undefined && !patch.view) {
        state.barPinned = false;
      }

      var next = snapshot();
      if (render) render(prev, next, opts);
      notify(next, prev);
    },

    // ---- legacy shims -------------------------------------------------------
    // Old imperative API. Migrated callers use set(); these remain only so any
    // straggler (dead overlay-modal code awaiting removal) stays inert instead
    // of throwing. Do not add new callers.
    revealFilters: function (opts) {
      window.HeaderState.set({ view: state.view === 'home' ? 'collection' : state.view });
      if (opts && typeof opts.callback === 'function') opts.callback();
    },
    enterModal: function (opts) {
      window.HeaderState.set({ view: 'collection' });
      if (opts && typeof opts.callback === 'function') opts.callback();
    },
    exitModal: function () {
      window.HeaderState.set({
        view: 'home',
        atTop: (window.__indexHomeScrollY || 0) <= 35,
      });
    },
    revealMain: noop, // setter (script-index-content.js) was removed long ago

    setNameDisplay: noop, // replaced by the renderer block below

    // Cart drawer — visual state is driven entirely through the
    // html/body.cart-render + .cart-open classes (CSS handles the rest).
    openCart: function () {
      if (state.cartOpen) return;
      var prev = snapshot();
      state.cartOpen = true;
      var next = snapshot();

      /* Crossfade vs instant swap.
       *
       * When the cart opens from a state where the LOGO IS NOT SHOWING (home
       * scrolled, collection / filter / viewall) the outgoing state has to go
       * somewhere visible: the filter bar and the solid black nav band fade out
       * while #cart-logo fades in, all on the drawer's own 0.54s curve. That is
       * what body.cart-fade-logo turns on — the CSS lives with each element
       * (#index-menu in navigation.liquid, #cart-logo in style-globals,
       * #nav-bg.nav-band in style-index) and runs in reverse on close.
       *
       * When the logo IS already up (home top, PDP) the class is withheld and
       * the swap stays instant: #index-logo is simply exchanged for the
       * identical #cart-logo, so fading the incoming one in from 0 would read
       * as the logo blinking out and back in.
       *
       * Index only — it is the one template with a hideable logo (.nav-hidden
       * is set by this renderer, which doesn't attach elsewhere) and the one
       * that has a filter bar / nav band to fade at all. */
      if (document.body.classList.contains('template-index') && !chromeFor(prev).logo) {
        document.body.classList.add('cart-fade-logo');
      } else {
        document.body.classList.remove('cart-fade-logo');
      }

      // Set the 0.54s transition inline so cart-open + cart-close fades stay
      // unified with the drawer's timing. Cleaned up in closeCart.
      ['index-logo', 'cart-count'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) el.style.transition = 'opacity 0.54s ease-in-out';
      });
      // The collection-modal title swaps INSTANTLY (no fade) when the cart
      // opens/closes — mirrors the modal's instant filter-bar → title swap, and
      // keeps it from lingering/fading over the cart logo.
      var titleSnap = document.getElementById('collection-modal-title');
      if (titleSnap) titleSnap.style.transition = 'none';

      // Two-phase open so the opacity fade actually runs: cart-render first
      // (display:block + scroll-lock + JS placement at opacity 0), then cart-open
      // on a later frame flips opacity → the transition animates. Without the
      // gap the mobile layers go display:none→opacity:1 in one recalc and pop in.
      document.documentElement.classList.add('cart-render');
      document.body.classList.add('cart-render');
      if (window.HeaderState.positionCartLayer) window.HeaderState.positionCartLayer();
      var commit = function () {
        // Guard against a close that landed inside the two-frame arming window
        // (common on touch right after add-to-cart). Without this, the stale
        // commit re-adds cart-open AFTER closeCart removed cart-render — the
        // desync that makes the × stick and the next tap open-then-close.
        if (!state.cartOpen) return;
        document.documentElement.classList.add('cart-open');
        document.body.classList.add('cart-open');
        document.dispatchEvent(new CustomEvent('cart:opened'));
      };
      // Two rAFs guarantee the display/transform commit has painted before the
      // opacity flip; desktop has no display:none so it can flip same-tick.
      requestAnimationFrame(function () { requestAnimationFrame(commit); });
      notify(next, prev);
    },
    closeCart: function () {
      if (!state.cartOpen) return;
      var prev = snapshot();
      state.cartOpen = false;
      var next = snapshot();
      // Drop cart-open → opacity fades out, but keep cart-render (display +
      // scroll-lock) until the fade finishes so it doesn't snap to display:none.
      document.documentElement.classList.remove('cart-open');
      document.body.classList.remove('cart-open');
      document.dispatchEvent(new CustomEvent('cart:closed'));
      setTimeout(function () {
        // Reopened during the fade-out window — keep it rendered so the new
        // openCart's cart-render isn't torn out from under it.
        if (state.cartOpen) return;
        document.documentElement.classList.remove('cart-render');
        document.body.classList.remove('cart-render');
        // Tear down spacers / clear transforms now that the layer is gone.
        if (window.HeaderState.positionCartLayer) window.HeaderState.positionCartLayer();
        // Clear the inline transition we set on open so other animations
        // aren't stuck with this value.
        ['index-logo', 'cart-count', 'collection-modal-title'].forEach(function (id) {
          var el = document.getElementById(id);
          if (el) el.style.transition = '';
        });
      }, 540);
      // Drop the crossfade class a beat AFTER the 540ms reverse fade rather than
      // inside the timer above: at exactly 540ms the logo's stepped visibility
      // and the bar's opacity are landing on their final frame, and pulling the
      // rule out on that same tick can clip it.
      setTimeout(function () {
        if (state.cartOpen) return; // reopened mid-fade — the class is in use again
        document.body.classList.remove('cart-fade-logo');
      }, 600);
      notify(next, prev);
    },

    // Renderer hookup (internal).
    _attachRenderer: function (fn) { render = fn; },
    _chromeFor: chromeFor,       // internal, renderer only
    _rawState: function () { return state; }, // internal, renderer only
  };
})();

/* ---------- go-home flag hygiene (all templates) ----------
   The persisted cl-nav-hidden flag exists so a reload / bfcache-back into a
   SCROLLED or modal state pre-paint-hides the logo (no flash). But a hard
   go-home NAVIGATION (the logo link on the information page / hard PDP /
   hard collection, #cart-logo, a footer logo outside the index scripts)
   lands on the index TOP, where the logo must be visible from the very first
   paint — a stale '1' made it start hidden and pop in ~300ms later when
   reconcileOnLoad settled. These links' destination is known at CLICK time,
   so clear the flag before the navigation starts. Capture phase, and safe on
   the index too: soft go-home paths re-persist their own state right after. */
document.addEventListener('click', function (e) {
  if (!e.target || !e.target.closest) return;
  if (e.target.closest('#index-logo a, #cart-logo a, .footer-logo-link')) {
    try { sessionStorage.setItem('cl-nav-hidden', '0'); } catch (_) {}
  }
}, true);

/* ---------- Cart layer positioning (mobile only) ----------
 *
 * The nav chrome stays position:fixed (compositor-pinned → never jitters during
 * scroll). Only the MOBILE cart is position:absolute (the iOS fixed+blur fix),
 * so it must be placed at the current viewport offset via JS. The cart only ever
 * shows with body scroll LOCKED, so a single placement on open (plus resize) is
 * enough — there is NO scroll listener, so nothing can jitter.
 *
 * Layout: drawer + backdrop at translateY(scrollY) (the viewport); the two 100vh
 * spacers offset ±innerHeight so they mask overscroll above/below. Desktop keeps
 * the cart position:fixed, so transforms are cleared there.
 */
(function () {
  function isMobile() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 573px)').matches);
  }

  // The two overscroll-mask spacers are NOT in the server-rendered DOM — they're
  // created here only while the mobile cart is open and torn down on close, so
  // they never linger in the body (and can't affect layout) when the cart is
  // shut. CSS (keyed by #cart-spacer-top / #cart-spacer-bottom) does the styling.
  function ensureSpacers(create) {
    ['cart-spacer-top', 'cart-spacer-bottom'].forEach(function (id) {
      var el = document.getElementById(id);
      if (create) {
        if (!el) {
          el = document.createElement('div');
          el.id = id;
          // Bottom mask gets an inner blur div (the outer carries the main
          // cart-body blur/tint; the inner adds the checkout-style second blur
          // so the overscroll-reveal matches the doubled blur of the checkout
          // strip above it). Styled by .cart-spacer-blur in style-globals.
          if (id === 'cart-spacer-bottom') {
            var inner = document.createElement('div');
            inner.className = 'cart-spacer-blur';
            el.appendChild(inner);
          }
          document.body.appendChild(el);
        }
      } else if (el) {
        el.parentNode.removeChild(el);
      }
    });
  }

  function positionCartLayer() {
    // cart-render spans the whole open→fade→close-fade window (cart-open is just
    // the opacity phase), so place the layers whenever it's present.
    var open = document.body.classList.contains('cart-render');
    var y = window.scrollY || window.pageYOffset || 0;
    // The old PDP modal pinned <body> at position:fixed; top:-scrollY, which
    // makes window.scrollY read 0. If the cart is opened OVER such a pin,
    // recover the real viewport offset from it so the absolute cart layers
    // land at the viewport, not the document top.
    if (document.body.style.position === 'fixed' && document.body.style.top) {
      y = -parseInt(document.body.style.top, 10) || 0;
    }
    var h = window.innerHeight;
    function setT(id, val) {
      var el = document.getElementById(id);
      if (el) el.style.transform = val;
    }
    if (open && isMobile()) {
      ensureSpacers(true);
      var base = 'translate3d(0,' + y + 'px,0)';
      setT('cart-backdrop', base);
      setT('cart-drawer', base);
      setT('cart-spacer-top', 'translate3d(0,' + (y - h) + 'px,0)');
      setT('cart-spacer-bottom', 'translate3d(0,' + (y + h) + 'px,0)');
    } else {
      // Closed, or desktop (where the cart is position:fixed): no transform.
      setT('cart-backdrop', '');
      setT('cart-drawer', '');
      ensureSpacers(false);
    }
  }

  window.HeaderState.positionCartLayer = positionCartLayer;
  // Place synchronously on open (scroll is locked, so the transform doesn't
  // animate — the CSS transition is opacity-only) and clear on close.
  document.addEventListener('cart:opened', positionCartLayer);
  document.addEventListener('cart:closed', positionCartLayer);
  window.addEventListener('resize', function () {
    if (document.body.classList.contains('cart-open')) positionCartLayer();
  });
})();

/* ---------- Renderer ----------
 *
 * The single writer of nav-chrome DOM. Subscribed to state via _attachRenderer;
 * derives the chrome from the record (chromeFor) and picks the transition from
 * (prev → next):
 *
 *   home atTop→scrolled    bar fades in 0.3s, logo fades out 0.3s
 *   home scrolled→atTop    bar fades out 0.3s, logo fades in 0.3s after 300ms
 *   logo click (pin)       logo fades out 0.3s, bar fades in 0.4s after 300ms
 *   any view change        instant snap (modal/PDP swaps are snaps by design)
 *
 * Also owns: scroll sensing (replaces the old IntersectionObserver sentinel +
 * `triggered` latch), the persisted resting state (.nav-hidden class +
 * sessionStorage flag, ALWAYS f(state) now), the nav band, the load/bfcache
 * reconcile, and the logo click dispatch (intents out).
 */
document.addEventListener('DOMContentLoaded', function () {
  var menu = document.getElementById('index-menu');
  if (!menu) return; // non-index page: no index chrome to render
  var filterBar = document.getElementById('filter-bar');
  var indexLogo = document.getElementById('index-logo');

  var hideLogoTimer = null;
  var showLogoTimer = null;
  var pinBarTimer = null;

  // ---------- persisted resting state ----------
  // TWO stores so a back/reload never flashes:
  //  • sessionStorage flag → read by theme.liquid's pre-paint script on a real
  //    reload (DOM is re-rendered fresh, no class yet).
  //  • the .nav-hidden class on <html>/<body> → bfcache restores DOM classes, so
  //    on a back-restore the CSS (pre-load.liquid) hides the logo on the very
  //    first frame, before any JS runs. visibility (not opacity) so the restore
  //    can't show a fade. The class is only added once the logo is settled-hidden
  //    (end of fade / instant hides) and removed before any fade-in, so it never
  //    fights the in-session opacity animations.
  function navFlag(hidden) {
    try { sessionStorage.setItem('cl-nav-hidden', hidden ? '1' : '0'); } catch (e) {}
  }
  function setNavHidden(hidden) {
    navFlag(hidden);
    document.documentElement.classList.toggle('nav-hidden', hidden);
    if (document.body) document.body.classList.toggle('nav-hidden', hidden);
  }

  // ---------- chrome derivation ----------
  // Hoisted into the state IIFE at the top of this file so openCart() derives
  // "was the logo showing?" from the exact same rule the renderer draws from.
  var chromeFor = window.HeaderState._chromeFor;

  function clearLogoTimers() {
    if (hideLogoTimer) { clearTimeout(hideLogoTimer); hideLogoTimer = null; }
    if (showLogoTimer) { clearTimeout(showLogoTimer); showLogoTimer = null; }
  }

  // ---------- logo primitives ----------
  function logoShowInstant() {
    if (!indexLogo) return;
    setNavHidden(false); // class first, or its visibility:hidden !important wins
    clearLogoTimers();
    indexLogo.style.transition = 'none';
    indexLogo.style.visibility = '';
    indexLogo.style.opacity = '1';
    indexLogo.style.pointerEvents = '';
    requestAnimationFrame(function () { indexLogo.style.transition = ''; });
  }
  function logoHideInstant() {
    if (!indexLogo) return;
    clearLogoTimers();
    indexLogo.style.transition = 'none';
    indexLogo.style.opacity = '0';
    indexLogo.style.visibility = 'hidden';
    indexLogo.style.pointerEvents = 'none';
    requestAnimationFrame(function () { indexLogo.style.transition = ''; });
    setNavHidden(true);
  }
  function logoFadeOut() {
    if (!indexLogo) return;
    // Persist the intent now, but add the .nav-hidden class only AFTER the fade
    // (in the timer) — adding it up front would apply CSS visibility:hidden
    // before the inline transition starts and snap the logo out instead.
    navFlag(true);
    clearLogoTimers();
    indexLogo.style.pointerEvents = 'none';
    indexLogo.style.transition = 'opacity 0.3s ease';
    indexLogo.style.opacity = '0';
    hideLogoTimer = setTimeout(function () {
      hideLogoTimer = null;
      indexLogo.style.visibility = 'hidden';
      indexLogo.style.opacity = '';
      indexLogo.style.transition = '';
      setNavHidden(true); // resting hidden → add the restorable class
    }, 300);
  }
  function logoFadeIn() {
    if (!indexLogo) return;
    // Suppressed while a timed reveal (footer-logo → home-top fade) owns the
    // logo — its 0.9s grid-synced fade must not be stomped by this 0.3s one.
    if (window.__logoTimedRevealActive) return;
    // This runs on a 300ms delay; re-check that the state still wants the logo
    // (a scroll-down or view change inside the window supersedes the timer).
    var s = window.HeaderState.state;
    if (!chromeFor(s).logo) return;
    if (s.view === 'home' && (window.scrollY || window.pageYOffset || 0) > 35) return;
    setNavHidden(false); // remove class first so the inline fade-in is visible
    clearLogoTimers();
    indexLogo.style.visibility = '';
    indexLogo.style.pointerEvents = '';
    indexLogo.style.opacity = '0';
    requestAnimationFrame(function () {
      indexLogo.style.transition = 'opacity 0.3s ease';
      indexLogo.style.opacity = '1';
      setTimeout(function () { indexLogo.style.transition = ''; }, 300);
    });
  }

  // Reveal the logo with a caller-chosen duration (used by the footer-logo
  // home-top fade so the logo eases in on the SAME 0.9s curve as the grid,
  // instead of the default 0.3s). Sets a guard so the state renderer's
  // logoFadeIn can't override mid-fade.
  var logoTimedRevealTimer = null;
  function revealLogoTimed(durationMs) {
    if (!indexLogo) return;
    window.__logoTimedRevealActive = true;
    clearLogoTimers();
    if (logoTimedRevealTimer) { clearTimeout(logoTimedRevealTimer); logoTimedRevealTimer = null; }
    setNavHidden(false); // remove .nav-hidden so the fade is visible
    indexLogo.style.transition = 'none';
    indexLogo.style.visibility = '';
    indexLogo.style.pointerEvents = '';
    indexLogo.style.opacity = '0';
    requestAnimationFrame(function () {
      indexLogo.style.transition = 'opacity ' + (durationMs / 1000) + 's ease-in-out';
      indexLogo.style.opacity = '1';
      logoTimedRevealTimer = setTimeout(function () {
        indexLogo.style.transition = '';
        window.__logoTimedRevealActive = false;
        logoTimedRevealTimer = null;
      }, durationMs + 50);
    });
  }
  window.HeaderState.revealLogoTimed = revealLogoTimed;
  window.__revealLogoTimed = revealLogoTimed; // legacy alias

  // ---------- menu (filter bar) primitives ----------
  function menuShowInstant() {
    // The bar is visible again — (re)measure group widths so they don't stack
    // if the last measure ran while it was display:none (deep-link/PDP load).
    var filterSection = document.getElementById('shopify-section-index-filters');
    if (filterSection) filterSection.style.visibility = '';
    menu.style.transition = 'none';
    menu.style.visibility = 'visible';
    menu.style.opacity = '1';
    menu.style.pointerEvents = 'auto';
    requestAnimationFrame(function () { menu.style.transition = ''; });
    if (window.__measureFilterWidths) window.__measureFilterWidths();
  }
  function menuHideInstant() {
    menu.style.transition = 'none';
    menu.style.opacity = '0';
    menu.style.pointerEvents = 'none';
    requestAnimationFrame(function () { menu.style.transition = ''; });
  }
  function menuFadeIn(durS) {
    if (window.__measureFilterWidths) window.__measureFilterWidths();
    // Only animate if it isn't already showing — re-running the 0→1 fade on an
    // already-visible bar reads as a flash.
    if (menu.style.opacity === '1') {
      menu.style.pointerEvents = 'auto';
      return;
    }
    menu.style.opacity = '0';
    requestAnimationFrame(function () {
      menu.style.transition = 'opacity ' + durS + 's ease';
      menu.style.opacity = '1';
      menu.style.pointerEvents = 'auto';
      setTimeout(function () { menu.style.transition = ''; }, durS * 1000);
    });
  }
  function menuFadeOut() {
    menu.style.transition = 'opacity 0.3s ease';
    menu.style.opacity = '0';
    menu.style.pointerEvents = 'none';
    setTimeout(function () { menu.style.transition = ''; }, 300);
  }

  // ---------- the render function ----------
  function render(prev, next, opts) {
    opts = opts || {};
    var had = chromeFor(prev);
    var want = chromeFor(next);

    if (!opts.force &&
        had.logo === want.logo && had.menu === want.menu) {
      return; // chrome unchanged (e.g. collection → filter, or atTop in a modal)
    }

    // Any pending pin/show timers describe the OLD state — cancel them.
    if (pinBarTimer) { clearTimeout(pinBarTimer); pinBarTimer = null; }
    if (showLogoTimer) { clearTimeout(showLogoTimer); showLogoTimer = null; }

    // Timed logo reveal (footer-logo go-home): claim the logo FIRST, before
    // any branch below runs, whatever the branch. revealLogoTimed sets the
    // __logoTimedRevealActive guard, and every other logo-shower (logoFadeIn,
    // logoShowInstant call sites) defers to it — so a forced re-ping after a
    // closer's own ping restarts the logo at opacity 0 in the same task and
    // no instant-shown frame ever paints. Handled here rather than per-branch
    // because a forced same-state ping takes the animated branch with zero
    // flips, which would otherwise ignore the option entirely.
    if (opts.logoRevealMs && want.logo) revealLogoTimed(opts.logoRevealMs);

    var instant = opts.transition === 'instant' || prev.view !== next.view;

    // Special-case: logo click pins the bar at the home top.
    // logo 0.3s out now, bar 0.4s in after 300ms (the classic swap).
    if (!instant && next.view === 'home' && next.barPinned && !prev.barPinned) {
      logoFadeOut();
      pinBarTimer = setTimeout(function () {
        pinBarTimer = null;
        var s = window.HeaderState.state;
        if (chromeFor(s).menu) menuFadeIn(0.4);
      }, 300);
      return;
    }

    // Home-view scroll transitions (animated).
    if (!instant && prev.view === 'home' && next.view === 'home') {
      if (want.menu && !had.menu) menuFadeIn(0.3);
      if (!want.menu && had.menu) menuFadeOut();
      if (!want.logo && had.logo) logoFadeOut();
      if (want.logo && !had.logo && !opts.logoRevealMs) {
        // Bar leaves first, logo returns after — the 300ms stagger the banner
        // (script-index-message.js) also syncs its reveal to. (A timed reveal
        // was already claimed at the top when logoRevealMs is set.)
        showLogoTimer = setTimeout(function () {
          showLogoTimer = null;
          logoFadeIn();
        }, 300);
      }
      return;
    }

    // Everything else (view changes, forced reconciles): instant snap.
    if (want.logo) {
      // Idempotent — safe to re-assert even when had.logo (forced renders).
      // Defers to a timed reveal claimed at the top of render.
      if (!window.__logoTimedRevealActive) logoShowInstant();
    } else {
      logoHideInstant();
    }
    if (want.menu) menuShowInstant();
    else menuHideInstant();
  }

  window.HeaderState._attachRenderer(render);

  /* ---------- scroll sensing ----------
     Replaces the old IntersectionObserver sentinel + `triggered` latch. A
     passive rAF-throttled listener compares the live scroll position to
     state.atTop and pings set() only on a mismatch — so it is self-healing by
     construction: any scroll the nav "missed" (programmatic scrollTo while a
     modal was up, the cart's overflow:clip zeroing the offset, …) is corrected
     by the next scroll event, and view-change pings re-derive atTop anyway.
     Only meaningful while home owns the document scroll. */
  var scrollRAF = null;
  function reconcileScroll() {
    scrollRAF = null;
    var s = window.HeaderState.state;
    if (s.view !== 'home') return;   // another view owns the document scroll
    if (s.cartOpen) return;          // scroll-locked; offset isn't home's own
    if (window.__navSettling) return; // load reconcile owns the chrome
    var atTop = (window.scrollY || window.pageYOffset || 0) <= 35;
    if (atTop === s.atTop) return;
    window.HeaderState.set({ atTop: atTop });
  }
  window.addEventListener('scroll', function () {
    if (scrollRAF) return;
    scrollRAF = requestAnimationFrame(reconcileScroll);
  }, { passive: true });

  // Belt for the cart's scroll-lock: overflow:clip can move the offset with no
  // scroll event to show for it. Once the lock is fully gone (540ms after
  // cart:closed), reconcile against wherever the document actually is.
  document.addEventListener('cart:closed', function () {
    setTimeout(reconcileScroll, 600);
  });

  /* ---------- nav band ----------
     The bar's black fill is painted by #nav-bg, NOT by #index-menu — a fill on
     #index-menu rides the opacity animation this file runs on it for the
     logo↔filter swap, so it renders at every intermediate value and the bar
     never looks solid (see style-index.css → #nav-bg.nav-band).

     Derived from the element rather than paired with every opacity write —
     one missed site is a stranded black strip. ON is immediate; OFF waits out
     the longest menu fade (0.4s) so the buttons never fade out over bare grid. */
  var navBg = document.getElementById('nav-bg');
  var bandOffTimer = null;
  var BAND_OFF_MS = 400;

  function syncNavBand() {
    if (!navBg) return;
    if (menu.style.opacity === '1') {
      if (bandOffTimer) { clearTimeout(bandOffTimer); bandOffTimer = null; }
      navBg.classList.add('nav-band');
    } else if (!bandOffTimer) {
      bandOffTimer = setTimeout(function () {
        bandOffTimer = null;
        if (menu.style.opacity !== '1') navBg.classList.remove('nav-band');
      }, BAND_OFF_MS);
    }
  }

  if (navBg && typeof MutationObserver !== 'undefined') {
    new MutationObserver(syncNavBand).observe(menu, {
      attributes: true,
      attributeFilter: ['style'],
    });
    syncNavBand(); // catch a bar already showing at wire-up (deep link / restore)
  }

  /* ---------- intents out: the logo click ----------
     The ONLY logo click handler in the codebase (full inversion). The nav
     holds the authoritative state, so it dispatches on it directly instead of
     three scripts racing on capture phase and checking each other:

       cart open   → hard home (the cart chrome's logo semantics, all templates)
       view 'pdp'  → emit 'nav:home'; script-index-product.js soft-restores
       home, top   → pin the bar (the classic logo → filter-bar swap)

     Modal views never get here — the logo is hidden and pointer-events:none.
     Non-index pages have no renderer; their logo is a native <a href="/">. */
  if (indexLogo) {
    indexLogo.style.cursor = 'pointer';
    indexLogo.addEventListener('click', function (e) {
      var s = window.HeaderState.state;
      if (s.cartOpen) {
        e.preventDefault();
        e.stopPropagation();
        // Only the logo IMAGE/link itself goes home. The rest of the strip is
        // negative space — including the area under the × icon (#cart-count is
        // pointer-events:none while the cart is open, so its click falls
        // through to the strip when the PDP's pointer-events:auto override is
        // active) — and negative space in the cart's top bar means CLOSE,
        // matching #cart-close-bar semantics everywhere else.
        if (e.target.closest('#index-logo img, #index-logo a')) {
          // Destination is the home TOP — clear the persisted hide so the
          // fresh index paints the logo from the first frame (see the go-home
          // flag hygiene listener above). Locale-aware root: a bare "/" costs
          // a second client-side hop through script-functions' language router.
          try { sessionStorage.setItem('cl-nav-hidden', '0'); } catch (_) {}
          window.location.href =
            (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        } else {
          window.HeaderState.closeCart();
        }
        return;
      }
      if (s.view === 'pdp') {
        e.preventDefault();
        e.stopPropagation();
        document.dispatchEvent(new CustomEvent('nav:home', { detail: { from: 'pdp' } }));
        return;
      }
      if (s.view === 'home' && s.atTop && !s.barPinned) {
        window.HeaderState.set({ barPinned: true });
      }
    });
  }

  // ---------- back-nav chrome re-sync ----------
  // On mobile, returning to the index from a hard-linked page leaves the
  // logo ↔ filter-bar state stale. Two restore paths (handled in pageshow
  // below): a bfcache HIT (DOM restored, no events re-fire) and a bfcache MISS
  // (real reload whose scroll restoration lands asynchronously).

  // hideOnly=true (every back/reload restore): only ever HIDE the logo when
  // scrolled — never force-show. Force-showing here caused the home-grid flash:
  // a pre-restoration scrollY read of 0 took the "at top" branch and showed the
  // logo, then the real scroll landed and re-hid it.
  function syncChromeToScroll(hideOnly) {
    var s = window.HeaderState.state;
    if (s.view !== 'home' || s.barPinned) return;
    var scrolled = (window.scrollY || window.pageYOffset || 0) > 35;
    if (!scrolled && hideOnly) return; // restore already shows the logo — leave it
    window.HeaderState.set({ atTop: !scrolled }, { transition: 'instant', force: true });
  }

  // Reconcile the chrome to the SETTLED scroll position once per load.
  //
  // Scroll restoration is async (fires AFTER load/pageshow) and iOS Safari
  // frequently MISREPORTS a back navigation's type as 'navigate' — so neither
  // an early scrollY read nor performance navType can tell us "are we at the
  // top?". Decide from BEHAVIOUR instead: if a scroll fires shortly after
  // load, the page was restored scrolled → hide-only reconcile (no flash). If
  // NO scroll fires, we're genuinely at the top → full reconcile, which may
  // show the logo / clear a stale pre-paint hide. ONE-SHOT: the listener must
  // be removed at the end of the window (on a bfcache restore the scroll is
  // re-applied silently, so an armed listener would linger until some later
  // unrelated scroll and mutate the chrome mid-animation).
  function reconcileOnLoad() {
    window.__navSettling = true; // suppress the scroll reconciler's transients
    var settled = false;
    function onScroll() {
      settled = true;
      window.removeEventListener('scroll', onScroll);
      window.__navSettling = false;
      syncChromeToScroll(true); // restoration moved us → hide-only
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(function () {
      window.removeEventListener('scroll', onScroll); // never let it linger
      if (!settled) {
        window.__navSettling = false;
        syncChromeToScroll(false); // no scroll fired → at top; may show
      }
      window.__navSettling = false;
    }, 300);
  }

  // Synchronously force the logo to the persisted hidden state. The inline
  // pre-paint script in theme.liquid only runs on a real load, NOT on a bfcache
  // restore — so on bfcache the logo could show for a frame before
  // reconcileOnLoad settles. pageshow fires BEFORE the restored frame paints,
  // so applying the flag here kills that flash. HIDE-only (safe in every
  // state); the directional reconcile follows in reconcileOnLoad.
  function applyNavHiddenInstant() {
    if (!indexLogo) return;
    var hidden = false;
    try { hidden = sessionStorage.getItem('cl-nav-hidden') === '1'; } catch (e) {}
    if (!hidden) return; // shown state is the restored default; leave it alone
    clearLogoTimers();
    indexLogo.style.transition = 'none';
    indexLogo.style.opacity = '0';
    indexLogo.style.visibility = 'hidden';
    indexLogo.style.pointerEvents = 'none';
    menu.style.transition = 'none';
    menu.style.visibility = 'visible';
    menu.style.opacity = '1';
    menu.style.pointerEvents = 'auto';
  }

  // One handler for BOTH initial load and bfcache restore (pageshow always
  // fires, after 'load'). On bfcache, hide synchronously first per the
  // persisted flag so the restored frame can't show the logo, then reconcile
  // once scroll settles.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) applyNavHiddenInstant();
    reconcileOnLoad();
  });

  // ---------- NAME pill (bar content, nav-owned markup) ----------
  // Pin the NAME pill to an arbitrary label — used for collections not in
  // the filter menu.
  window.HeaderState.setNameDisplay = function (labelText) {
    var nameDropdown = document.querySelector("[data-filter='name']");
    if (!nameDropdown || !labelText) return;
    var container = nameDropdown.closest('.dropdown-button-container');
    var filterGroup = container ? container.closest('.filter-group') : null;
    var displaySpan = container ? container.querySelector('.selected-filter') : null;
    var placeholder = container ? container.querySelector('.filter-placeholder') : null;
    if (!container || !displaySpan) return;

    displaySpan.textContent = labelText;
    if (placeholder) placeholder.style.display = 'none';

    var textWidth = displaySpan.offsetWidth;
    container.style.width = textWidth + 'px';
    nameDropdown.style.width = textWidth + 'px';
    if (filterGroup) filterGroup.style.width = textWidth + 'px';

    filterBar.classList.add('name-active');
    filterBar.classList.remove('category-finish-active');
  };
});
